import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import StatCard from "../../components/admin/StatCard";
import toast from "react-hot-toast";
import { getForumAdminData, moderateForumEntity } from "../../api/axios";

const POST_STATUS_STYLES = {
  published: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  hidden: "bg-slate-100 text-slate-600 border-slate-200",
  deleted: "bg-red-100 text-red-700 border-red-200",
};

function CommentRow({ c, act, showPostTitle }) {
  return (
    <div className="p-4 flex flex-col md:flex-row md:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${POST_STATUS_STYLES[c.status] || POST_STATUS_STYLES.hidden}`}>{c.status}</span>
          {showPostTitle && <span className="text-xs text-[#707975]">on "{c.post_title}"</span>}
        </div>
        <div className="font-semibold text-[#191c1d] text-sm mt-1">{c.author_name || "Unknown"}</div>
        <div className="text-sm text-[#707975] mt-0.5">{c.comment}</div>
      </div>
      <div className="flex gap-2 shrink-0 flex-wrap">
        {c.status !== "published" && (
          <button onClick={() => act("comment", c.id, "published")} className="px-2.5 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition">Show</button>
        )}
        {c.status !== "hidden" && (
          <button onClick={() => act("comment", c.id, "hidden")} className="px-2.5 py-1.5 rounded-lg border border-[#bfc9c4] text-xs font-semibold text-[#3f4945] hover:bg-gray-50 transition">Hide</button>
        )}
        {c.status !== "deleted" && (
          <button onClick={() => act("comment", c.id, "deleted", "Delete this comment permanently? This cannot be undone.")} className="px-2.5 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 transition">Delete</button>
        )}
      </div>
    </div>
  );
}

export default function ForumModeration() {
  const [data, setData] = useState({ posts: [], comments: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("posts");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [expandedPostId, setExpandedPostId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getForumAdminData();
      setData(res.data || { posts: [], comments: [], stats: {} });
    } catch (err) {
      toast.error(err.message || "Failed to load forum data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (entity, id, newStatus, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await moderateForumEntity(entity, id, newStatus);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e.message || "Failed");
    }
  };

  const filteredPosts = useMemo(() => {
    return (data.posts || []).filter((p) => {
      const q = search.trim().toLowerCase();
      const matchesSearch = !q || [p.title, p.author_name, p.content].some((v) => String(v || "").toLowerCase().includes(q));
      const matchesStatus = !status || p.status === status;
      return matchesSearch && matchesStatus;
    });
  }, [data.posts, search, status]);

  const filteredComments = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.comments || [];
    return (data.comments || []).filter((c) => [c.comment, c.author_name, c.post_title].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [data.comments, search]);

  const stats = data.stats || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#00342b]">Forum Moderation</h2>
          <p className="text-sm text-[#3f4945] mt-1">Manage posts, comments, and community engagement</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <StatCard title="Total Posts" value={stats.total_posts ?? 0} subtitle="All forum posts" />
          <StatCard title="Published" value={stats.published ?? 0} subtitle="Visible to the public" />
          <StatCard title="Pending" value={stats.pending ?? 0} subtitle="Awaiting review" />
          <StatCard title="Hidden" value={stats.hidden ?? 0} subtitle="Hidden by moderators" />
          <StatCard title="Total Comments" value={stats.total_comments ?? 0} subtitle="Across all posts" />
        </div>

        <div className="flex gap-3 flex-wrap">
          <button onClick={() => setTab("posts")} className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${tab === "posts" ? "bg-[#00342b] text-white border-[#00342b]" : "bg-white text-[#3f4945]"}`}>Posts ({data.posts?.length || 0})</button>
          <button onClick={() => setTab("comments")} className={`px-4 py-2 rounded-full border text-sm font-semibold transition ${tab === "comments" ? "bg-[#00342b] text-white border-[#00342b]" : "bg-white text-[#3f4945]"}`}>All Comments ({data.comments?.length || 0})</button>
        </div>

        <div className="bg-white rounded-2xl border border-[#e1e3e4] p-4 grid gap-3 md:grid-cols-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === "posts" ? "Search title / author / content..." : "Search comment / author / post..."}
            className="border border-[#bfc9c4] rounded-xl px-4 py-3 md:col-span-3 text-sm focus:ring-2 focus:ring-[#00342b] outline-none"
          />
          {tab === "posts" && (
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[#bfc9c4] rounded-xl px-4 py-3 bg-white text-sm">
              <option value="">All Status</option>
              <option value="published">✅ Published</option>
              <option value="pending">⏳ Pending</option>
              <option value="hidden">⛔ Hidden</option>
            </select>
          )}
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-[#e1e3e4] p-10 text-center text-[#3f4945]">Loading…</div>
        ) : tab === "posts" ? (
          <div className="bg-white rounded-2xl border border-[#e1e3e4] divide-y divide-[#f0f1f1]">
            {filteredPosts.map((p) => {
              const postComments = (data.comments || []).filter((c) => c.post_id === p.id);
              const isExpanded = expandedPostId === p.id;
              return (
                <div key={p.id}>
                  <div className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase border ${POST_STATUS_STYLES[p.status] || POST_STATUS_STYLES.hidden}`}>{p.status}</span>
                        <span className="text-xs text-[#707975] uppercase tracking-wide">{(p.type || "").replace("_", " ")}</span>
                      </div>
                      <div className="font-bold text-[#191c1d] mt-1.5">{p.title}</div>
                      <div className="text-sm text-[#707975] mt-0.5">By {p.author_name || "Unknown"}</div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-[#707975]">
                        <span>👁 {p.views ?? 0} views</span>
                        <span>♥ {p.like_count ?? 0} likes</span>
                        <button onClick={() => setExpandedPostId(isExpanded ? null : p.id)} className="hover:text-[#00342b] hover:underline font-semibold">
                          💬 {p.comment_count ?? 0} comments {postComments.length > 0 && (isExpanded ? "▲" : "▼")}
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0 flex-wrap">
                      {p.status !== "published" && (
                        <button onClick={() => act("post", p.id, "published")} className="px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition">Publish</button>
                      )}
                      {p.status !== "hidden" && (
                        <button onClick={() => act("post", p.id, "hidden")} className="px-3 py-2 rounded-lg border border-[#bfc9c4] text-xs font-semibold text-[#3f4945] hover:bg-gray-50 transition">Hide</button>
                      )}
                      {p.status !== "deleted" && (
                        <button onClick={() => act("post", p.id, "deleted", `Delete "${p.title}" permanently? This cannot be undone.`)} className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 transition">Delete</button>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="bg-[#f7f9f8] border-t border-[#e1e3e4] divide-y divide-[#e9ecea] pl-5 md:pl-12">
                      {postComments.map((c) => <CommentRow key={c.id} c={c} act={act} showPostTitle={false} />)}
                      {!postComments.length && <p className="text-[#a0aba5] text-sm p-4">No comments on this post.</p>}
                    </div>
                  )}
                </div>
              );
            })}
            {!filteredPosts.length && <p className="text-[#a0aba5] text-center p-10">No posts found.</p>}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#e1e3e4] divide-y divide-[#f0f1f1]">
            {filteredComments.map((c) => <CommentRow key={c.id} c={c} act={act} showPostTitle />)}
            {!filteredComments.length && <p className="text-[#a0aba5] text-center p-10">No comments found.</p>}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
